import React, { useEffect, useState } from 'react';
import { AlertCircle, Building2, Loader, Save } from 'lucide-react';
import {
  expertProfileApi,
  SolicitorCredentials,
  EMPTY_SOLICITOR_CREDENTIALS,
} from '../services/expertProfileApi';

export const FirmDetailsPage: React.FC = () => {
  const [form, setForm] = useState<SolicitorCredentials>({ ...EMPTY_SOLICITOR_CREDENTIALS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await expertProfileApi.get();
      const c = data.credentials || {};
      setForm({
        firmName: c.firmName ?? null,
        firmAddress: c.firmAddress ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        fullName: c.fullName ?? null,
        jobTitle: c.jobTitle ?? null,
        sraNumber: c.sraNumber ?? null,
        companyNumber: c.companyNumber ?? null,
      });
    } catch {
      // No profile yet — show empty form
    } finally {
      setLoading(false);
    }
  };

  const update = <K extends keyof SolicitorCredentials>(key: K, value: SolicitorCredentials[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form.firmName?.trim() || !form.firmAddress?.trim() || !form.email?.trim() || !form.phone?.trim()) {
      setError('Firm name, address, email, and phone are required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      // Backend treats `credentials` as JSONB — we send only the solicitor-relevant fields.
      await expertProfileApi.save(form as unknown as import('../services/expertProfileApi').ExpertCredentials);
      setSuccess('Firm details saved successfully.');
      setDirty(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message || 'Failed to save firm details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-medical-900 dark:text-medical-100 mb-2">
            Firm Details
          </h1>
          <p className="text-medical-600 dark:text-medical-400">
            Used to populate letterhead and sign-off blocks on letters of instruction, letters of claim,
            and other drafts.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-800 dark:text-emerald-300">{success}</p>
          </div>
        )}

        {/* Firm details — required */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-medical-900 dark:text-medical-100 mb-4">
            <Building2 className="h-5 w-5 text-primary-500" />
            Firm details
          </h2>
          <div className="space-y-4">
            <Field label="Firm name" required>
              <input
                type="text"
                className="input-field"
                value={form.firmName ?? ''}
                onChange={e => update('firmName', e.target.value || null)}
                placeholder="e.g. Hargreaves & Co Solicitors"
              />
            </Field>
            <Field label="Firm address" required>
              <textarea
                className="input-field"
                rows={3}
                value={form.firmAddress ?? ''}
                onChange={e => update('firmAddress', e.target.value || null)}
                placeholder="e.g. 12 High Street, London EC1A 1AA"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input
                  type="email"
                  className="input-field"
                  value={form.email ?? ''}
                  onChange={e => update('email', e.target.value || null)}
                  placeholder="enquiries@firm.co.uk"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  type="tel"
                  className="input-field"
                  value={form.phone ?? ''}
                  onChange={e => update('phone', e.target.value || null)}
                  placeholder="020 7000 0000"
                />
              </Field>
            </div>
          </div>
        </div>

        {/* Sender — optional */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100 mb-1">
            Sender (optional)
          </h2>
          <p className="text-sm text-medical-500 dark:text-medical-400 mb-4">
            Populate when a named solicitor signs the document. Leave blank to send on behalf of the firm.
          </p>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name">
                <input
                  type="text"
                  className="input-field"
                  value={form.fullName ?? ''}
                  onChange={e => update('fullName', e.target.value || null)}
                  placeholder="e.g. Eleanor Hargreaves"
                />
              </Field>
              <Field label="Job title">
                <input
                  type="text"
                  className="input-field"
                  value={form.jobTitle ?? ''}
                  onChange={e => update('jobTitle', e.target.value || null)}
                  placeholder="e.g. Senior Associate"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SRA number">
                <input
                  type="text"
                  className="input-field"
                  value={form.sraNumber ?? ''}
                  onChange={e => update('sraNumber', e.target.value || null)}
                  placeholder="e.g. 612345"
                />
              </Field>
              <Field label="Company number">
                <input
                  type="text"
                  className="input-field"
                  value={form.companyNumber ?? ''}
                  onChange={e => update('companyNumber', e.target.value || null)}
                  placeholder="e.g. OC301234 (LLP) or 12345678 (Ltd)"
                />
              </Field>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span>{saving ? 'Saving…' : 'Save firm details'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
